import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getPatientsListAPI,
  syncProfileAPI,
  getColumnsAPI,
  saveColumnsAPI,
  getMdacPatientsListAPI,
  getMdacPatientsOnboardingListAPI,
  updateGridSettingsAPI,
  getGridSettingsAPI,
  getAppointmentListAPI,
  mwiseRatingAPI,
  userReferralAPI,
} from 'services/adminServices';
import { handleMessage } from 'shared/api/ErrorMessages';

interface Error {
  data: any;
}

export const getPatientsList = createAsyncThunk(
  'GET_PATIENTS_LIST',
  async (data: any, { rejectWithValue }: any) => {
    try {
      const response = await getPatientsListAPI(data);
      return response;
    } catch (err: any) {
      const e = err as Error;
      const status = e?.data?.errorCode;
      const message = handleMessage(status);
      const error = {
        err: err.data,
        message,
        show: false,
      };
      return rejectWithValue(error);
    }
  }
);

export const getAppointmentList = createAsyncThunk(
  'GET_APPOINTMENT_LIST',
  async (data: any, { rejectWithValue }: any) => {
    try {
      const response = await getAppointmentListAPI(data);
      return response;
    } catch (err: any) {
      const e = err as Error;
      const status = e?.data?.errorCode;
      const message = handleMessage(status);
      const error = {
        err: err.data,
        message,
        show: false,
      };
      return rejectWithValue(error);
    }
  }
);

export const getMdacPatientsList = createAsyncThunk(
  'GET_MDAC_PATIENTS_LIST',
  async (data: any, { rejectWithValue }: any) => {
    try {
      const response = await getMdacPatientsListAPI(data);
      return response;
    } catch (err: any) {
      const e = err as Error;
      const status = e?.data?.errorCode;
      const message = handleMessage(status);
      const error = {
        err: err.data,
        message,
        show: false,
      };
      return rejectWithValue(error);
    }
  }
);

export const getMdacPatientsOnboardingList = createAsyncThunk(
  'GET_MDAC_PATIENTS__ONBOARDING_LIST',
  async (data: any, { rejectWithValue }: any) => {
    try {
      const response = await getMdacPatientsOnboardingListAPI(data);
      return response;
    } catch (err: any) {
      const e = err as Error;
      const status = e?.data?.errorCode;
      const message = handleMessage(status);
      const error = {
        err: err.data,
        message,
        show: false,
      };
      return rejectWithValue(error);
    }
  }
);

export const getGridSettings = createAsyncThunk(
  'GET_GRID_SETTING',
  async (data: any, { rejectWithValue }: any) => {
    try {
      const response = await getGridSettingsAPI(data);
      return response;
    } catch (err: any) {
      const e = err as Error;
      const status = e?.data?.errorCode;
      const message = handleMessage(status);
      const error = {
        err: err.data,
        message,
        show: false,
      };
      return rejectWithValue(error);
    }
  }
);

export const updateGridSettings = createAsyncThunk(
  'GET_GRID_SETTING',
  async (data: any, { rejectWithValue }: any) => {
    try {
      const response = await updateGridSettingsAPI(data);
      return response;
    } catch (err: any) {
      const e = err as Error;
      const status = e?.data?.errorCode;
      const message = handleMessage(status);
      const error = {
        err: err.data,
        message,
        show: false,
      };
      return rejectWithValue(error);
    }
  }
);

export const syncProfile = createAsyncThunk(
  'SYNC_PROFILE',
  async (patientId: number, { rejectWithValue }: any) => {
    try {
      const response = await syncProfileAPI(patientId);
      return response;
    } catch (err: any) {
      const e = err as Error;
      const status = e?.data?.errorCode;
      const message = handleMessage(status);
      const error = {
        err: err.data,
        message,
        show: false,
      };
      return rejectWithValue(error);
    }
  }
);

export const mwiseRating = createAsyncThunk('MWISE_RATING', async ({ rejectWithValue }: any) => {
  try {
    const response = await mwiseRatingAPI();
    return response;
  } catch (err: any) {
    const e = err as Error;
    const status = e?.data?.errorCode;
    const message = handleMessage(status);
    const error = {
      err: err.data,
      message,
      show: false,
    };
    return rejectWithValue(error);
  }
});

export const referalSource = createAsyncThunk(
  'USER_REFERRAL_SOURCE',
  async ({ rejectWithValue }: any) => {
    try {
      const response = await userReferralAPI();
      return response;
    } catch (err: any) {
      const e = err as Error;
      const status = e?.data?.errorCode;
      const message = handleMessage(status);
      const error = {
        err: err.data,
        message,
        show: false,
      };
      return rejectWithValue(error);
    }
  }
);

export const getColumns = createAsyncThunk(
  'GET_COLUMNS',
  async (id: number, { rejectWithValue }: any) => {
    try {
      const response = await getColumnsAPI(id);
      return response;
    } catch (err: any) {
      const e = err as Error;
      const status = e?.data?.errorCode;
      const message = handleMessage(status);
      const error = {
        err: err.data,
        message,
        show: false,
      };
      return rejectWithValue(error);
    }
  }
);

export const saveColumns = createAsyncThunk(
  'SAVE_COLUMNS',
  async (data: any, { rejectWithValue }: any) => {
    try {
      const response = await saveColumnsAPI(data);
      return response;
    } catch (err: any) {
      const e = err as Error;
      const status = e?.data?.errorCode;
      const message = handleMessage(status);
      const error = {
        err: err.data,
        message,
        show: false,
      };
      return rejectWithValue(error);
    }
  }
);

interface Loader {
  isUserFetched: boolean;
}

interface StateI {
  loaders: Loader;
  error: undefined;
}

const initialState = {
  loaders: {
    isUserFetched: false,
  },
  error: undefined,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearError(state: StateI) {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPatientsList.pending, (state: StateI) => {
      state.loaders.isUserFetched = true;
    });
    builder.addCase(getPatientsList.fulfilled, (state: StateI) => {
      state.loaders.isUserFetched = false;
    });
    builder.addCase(getPatientsList.rejected, (state: StateI) => {
      state.loaders.isUserFetched = false;
    });
  },
});

export default adminSlice.reducer;
